import { EmptyTablePlaceholder } from '@openx/components-core/lib/TablePlaceholder/EmptyTablePlaceholder.tsx';
import { SelectableTable } from '@openx/components-table/SelectableTable/SelectableTable.tsx';
import { Table, TableName, type TableProps } from '@openx/components-table/Table.tsx';
import type { ListSegment } from '@openx/types/segments.ts';
import type { ToggleSelect } from '@openx/types/selectableTable.ts';
import type { AllTableCriteria, ChangeCriteriaHandler, CriteriaDimension } from '@openx/types/tableCriteria.ts';
import { useMemo } from 'react';

import type { SegmentsListFilters } from '../../types.ts';
import { useSegmentContext } from '../utils/context/SegmentContext.tsx';

import { SegmentsTopBar } from './SegmentsTopBar.tsx';

const SegmentsSelectableTable = <T extends Partial<ListSegment>>() =>
  SelectableTable<T, SegmentsListFilters, TableProps<T, SegmentsListFilters>>(Table);

type Props = {
  criteria: AllTableCriteria<SegmentsListFilters>;
  handleCriteriaChange: ChangeCriteriaHandler<CriteriaDimension, SegmentsListFilters>;
  handleFiltersChange: (filters: { dimension: typeof CriteriaDimension.FILTERS; value: SegmentsListFilters }) => void;
  handleSelectionChange: ToggleSelect<ListSegment>;
  loading: boolean;
  segments: ListSegment[];
  selectedSegments: ListSegment[];
  setCriteria: (criteria: AllTableCriteria<SegmentsListFilters>) => void;
};
export const SegmentsList = ({
  criteria,
  loading,
  segments,
  selectedSegments,
  handleCriteriaChange,
  handleFiltersChange,
  handleSelectionChange,
}: Props) => {
  const { useSegmentTableFetch } = useSegmentContext();
  const phrase = criteria.filters?.phrase || '';
  const { columns, filtersConfig, rowActions } = useSegmentTableFetch(phrase);

  const SegmentsTable = useMemo(() => SegmentsSelectableTable<ListSegment>(), []);

  const isTableEmpty = !loading && !criteria.filters && segments.length === 0;

  return (
    <>
      <SegmentsTopBar filtersConfig={filtersConfig} criteria={criteria} onChange={handleFiltersChange} />
      {isTableEmpty ? (
        <EmptyTablePlaceholder />
      ) : (
        <SegmentsTable
          name={TableName.SEGMENTS}
          data={segments}
          columns={columns}
          criteria={criteria}
          loading={loading}
          onCriteriaChange={handleCriteriaChange}
          rowActions={rowActions}
          noItemsLabel={() => 'No segments found'}
          selectedItems={selectedSegments}
          toggleSelect={handleSelectionChange}
        />
      )}
    </>
  );
};
